import { Box, Flex, Heading, HStack, Icon, Stack, Tag, Text } from "@chakra-ui/react";
import type { Moment } from "moment";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import {
  BidStatus,
  type PortalAwardOutcomeDto,
  PortalLifecycleStatus,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { awardDecisionReached } from "../../../supplier/myBids";
import { BidStatusTag } from "../../../sourcing-event/BidStatusTag";
import { PortalLifecycleTag } from "../components/PortalLifecycleTag";
import { getBuyerInitials, getTimeLeftMeta } from "../utils/displayFormatters";
import { effectiveDeadlineIso } from "../utils/effectiveDeadline";
import { useOpenPortalRound } from "./useOpenPortalRound";
import { isDesignContest } from "../../buyer/wizard/utils/designContest";

export const EventHero = ({
  event,
  now,
  ownBidStatus,
  ownDeliveredBidStatus,
  ownAwardOutcome,
  ownStandstillUntil,
}: {
  event: PortalSourcingEventDetailDto;
  now: Moment;
  ownBidStatus?: BidStatus;
  ownDeliveredBidStatus?: BidStatus | null;
  ownAwardOutcome?: PortalAwardOutcomeDto | null;
  ownStandstillUntil?: string | null;
}) => {
  const { t } = useTranslation();
  const designContest = isDesignContest(event.procedureType);
  const { round: openRound, exit, isLoading: roundsLoading, isError: roundsFailed } = useOpenPortalRound(event, ownAwardOutcome);
  const isOpen = event.lifecycle.status === PortalLifecycleStatus.Open || openRound != null;
  const shownBidStatus =
    !isOpen && ownBidStatus === BidStatus.Draft && ownDeliveredBidStatus ? ownDeliveredBidStatus : ownBidStatus;
  // Null during the qualification window — there is no active deadline to count down to.
  const deadlineIso = openRound?.deadline ?? effectiveDeadlineIso(event);
  const timeLeftMeta = deadlineIso != null ? getTimeLeftMeta(deadlineIso, t, now) : null;

  return (
    <Stack spacing="4">
      <Heading as="h1" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold" lineHeight="1.2">
        {event.title}
      </Heading>

      <HStack spacing="3" align="center" flexWrap="wrap" rowGap="2">
        {openRound ? (
          <Tag variant="outline" colorScheme="green" size="sm">
            {t("Round {{round}} open", { round: openRound.roundNumber })}
          </Tag>
        ) : (
          <PortalLifecycleTag
            status={event.lifecycle.status}
            stage={event.competitionStage}
            size="sm"
            designContest={designContest}
          />
        )}

        {shownBidStatus && !roundsLoading && !roundsFailed && (
          <>
            <MetaSeparator />
            <HStack spacing="2">
              <Text fontSize="sm" color="smMuted">
                {designContest ? t("My entry") : t("My bid")}
              </Text>
              <BidStatusTag
                status={shownBidStatus}
                awarded={awardDecisionReached(event.lifecycle.status, ownAwardOutcome)}
                standstillUntil={ownStandstillUntil}
                missedRound={exit?.reason === "noAnswer"}
                declinedRound={exit?.reason === "declined"}
                size="sm"
              />
            </HStack>
          </>
        )}

        {isOpen && timeLeftMeta != null && (
          <>
            <MetaSeparator />
            <HStack spacing="1.5" color={timeLeftMeta.color}>
              <Icon as={FiClock} boxSize="3.5" color={timeLeftMeta.color} />
              <Text fontSize="sm">{timeLeftMeta.label}</Text>
            </HStack>
          </>
        )}

        <MetaSeparator />

        <HStack spacing="2" color="smMuted">
          <Flex
            width="20px"
            height="20px"
            rounded="md"
            bg="smBackgroundSecondary"
            color="smMuted"
            fontSize="10px"
            fontWeight="bold"
            align="center"
            justify="center"
          >
            {getBuyerInitials(event.buyerName)}
          </Flex>
          <Text fontSize="sm" fontWeight="semibold">
            {event.buyerName}
          </Text>
        </HStack>

        {event.referenceId && (
          <>
            <MetaSeparator />
            <Text fontSize="sm" color="smMuted">
              {event.referenceId}
            </Text>
          </>
        )}
      </HStack>
    </Stack>
  );
};

const MetaSeparator = () => (
  <Box height="14px" width="1px" bg="smBorder" display={{ base: "none", sm: "block" }} />
);
